import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  IAuditoriaCaixaResultado,
  IItemSaldoCredorCaixa,
  IItemOperacaoEspecie,
  IItemBeneficioIndireto,
  ILancamentoCaixa,
} from '../interfaces/IAuditoriaCaixa';
import { formatCurrency, formatDate } from '../utils/formatters';

interface Props {
  resultado: IAuditoriaCaixaResultado;
}

// COR-300: drill-down a nível de lançamento — o Saldo Credor e as Operações em Espécie
// precisam mostrar não só o total consolidado, mas os lançamentos reais que o compuseram.
const LIMITE_LANCAMENTOS_VISIVEIS = 10;

const BlocoLancamentosRelacionados: React.FC<{ lancamentos: ILancamentoCaixa[] }> = ({ lancamentos }) => {
  const [mostrarTodos, setMostrarTodos] = useState(false);

  if (!lancamentos || lancamentos.length === 0) return null;

  const visiveis = mostrarTodos ? lancamentos : lancamentos.slice(0, LIMITE_LANCAMENTOS_VISIVEIS);

  return (
    <div className='mt-2'>
      <strong className='fs-8'>Lançamentos Relacionados ({lancamentos.length}):</strong>
      <div className='table-responsive mt-1'>
        <table className='table table-sm' style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>Data</th>
              <th>Nº Lançamento</th>
              <th>Conta</th>
              <th>Histórico</th>
              <th className='text-end'>Débito</th>
              <th className='text-end'>Crédito</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((lanc, idx) => (
              <tr key={`${lanc.numeroLancamento}-${lanc.data}-${idx}`}>
                <td>{formatDate(lanc.data)}</td>
                <td><code>{lanc.numeroLancamento || '-'}</code></td>
                <td><code>{lanc.contaContabil}</code></td>
                <td>{lanc.historico}</td>
                <td className='text-end'>{lanc.valorDebito > 0 ? formatCurrency(lanc.valorDebito) : '-'}</td>
                <td className='text-end'>{lanc.valorCredito > 0 ? formatCurrency(lanc.valorCredito) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {lancamentos.length > LIMITE_LANCAMENTOS_VISIVEIS && !mostrarTodos && (
        <button
          type='button'
          className='btn btn-link btn-sm p-0 mt-1'
          style={{ fontSize: 12 }}
          onClick={() => setMostrarTodos(true)}
        >
          + {lancamentos.length - LIMITE_LANCAMENTOS_VISIVEIS} lançamento(s) adicional(is)
        </button>
      )}
    </div>
  );
};

// COR-240: colunas da exportação das ocorrências de Benefício Indireto — pedidas explicitamente
// no ticket: data, conta, histórico original, valor, lançamento ECD original, lançamento de
// contrapartida, histórico da contrapartida, beneficiário identificado, classificação e
// justificativa. CSV (";", compatível Excel pt-BR) e XLSX compartilham a mesma montagem de linhas.
interface LinhaExportacaoBeneficioIndireto {
  Empresa: string;
  Data: string;
  'Conta Contábil': string;
  'Descrição da Conta': string;
  'Histórico Original': string;
  'Valor (R$)': string;
  'Lançamento ECD Original': string;
  'Lançamento de Contrapartida': string;
  'Histórico da Contrapartida': string;
  'Beneficiário Identificado': string;
  Classificação: string;
  Status: string;
  'Impacto Fiscal Estimado (R$)': string;
  Justificativa: string;
}

const montarLinhasExportacaoBeneficioIndireto = (
  itens: IItemBeneficioIndireto[],
  rotuloTipoAchado: (tipoAchado: string) => string
): LinhaExportacaoBeneficioIndireto[] =>
  itens.map(item => ({
    Empresa: item.empresaNome,
    Data: formatDate(item.dataLancamento),
    'Conta Contábil': item.contaContabil,
    'Descrição da Conta': item.descricaoConta,
    'Histórico Original': item.historico,
    'Valor (R$)': item.valor.toFixed(2).replace('.', ','),
    'Lançamento ECD Original': item.numeroLancamento || '',
    'Lançamento de Contrapartida': item.devolucaoVinculada?.numeroLancamentoDevolucao || '',
    'Histórico da Contrapartida': item.devolucaoVinculada?.historicoDevolucao || '',
    'Beneficiário Identificado': item.devolucaoVinculada?.beneficiarioIdentificado || '',
    Classificação: rotuloTipoAchado(item.tipoAchado),
    Status: item.status,
    'Impacto Fiscal Estimado (R$)': item.impactoFiscalTotal.toFixed(2).replace('.', ','),
    Justificativa: item.mensagemDiagnostico,
  }));

const exportarCSVBeneficioIndireto = (linhas: LinhaExportacaoBeneficioIndireto[], nomeArquivo: string) => {
  if (linhas.length === 0) return;
  const cabecalho = Object.keys(linhas[0]);
  const corpo = linhas.map(linha => Object.values(linha));
  const csv = [cabecalho, ...corpo]
    .map(row => row.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(';'))
    .join('\r\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  saveAs(blob, `${nomeArquivo}.csv`);
};

const exportarXLSXBeneficioIndireto = (linhas: LinhaExportacaoBeneficioIndireto[], nomeArquivo: string) => {
  if (linhas.length === 0) return;
  const ws = XLSX.utils.json_to_sheet(linhas);
  const wb = { Sheets: { 'Benefício Indireto': ws }, SheetNames: ['Benefício Indireto'] };
  const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
  saveAs(blob, `${nomeArquivo}.xlsx`);
};

const getIconeStatus = (status: string) => {
  switch (status) {
    case 'ERRO':
      return 'bi-exclamation-circle-fill';
    case 'ALERTA':
      return 'bi-exclamation-triangle-fill';
    case 'AVISO':
      return 'bi-info-circle-fill';
    default:
      return 'bi-check-circle-fill';
  }
};

// Retorna true apenas para os tipos com evidência suficiente para estimar impacto fiscal.
// AVISO (SEM_IDENTIFICACAO_SUFICIENTE, PENDENTE_VALIDACAO, AJUSTE_FIM_MES) não têm impacto
// confirmado e não devem figurar no total do card financeiro (COR-50).
const temEvidenciaFiscal = (tipoAchado: string): boolean =>
  tipoAchado === 'POSSIVEL_BENEFICIO_INDIRETO' || tipoAchado === 'BENEFICIO_INDIRETO_CONFIRMADO';

const labelTipoAchado = (tipoAchado: string): string => {
  switch (tipoAchado) {
    case 'POSSIVEL_BENEFICIO_INDIRETO':
      return 'Possível BI';
    case 'BENEFICIO_INDIRETO_CONFIRMADO':
      return 'BI Confirmado';
    case 'AJUSTE_FIM_MES':
      return 'Ajuste fim/mês';
    case 'SEM_IDENTIFICACAO_SUFICIENTE':
      return 'Sem identificação';
    case 'PENDENTE_VALIDACAO':
      return 'Pendente validação';
    case 'CONCILIADO_POR_DEVOLUCAO':
      return 'Conciliado (devolução/estorno)';
    default:
      return tipoAchado;
  }
};

// Conta erros/alertas/avisos de qualquer lista de itens com campo status (COR-51).
const contarStatus = (items: Array<{ status: string }>) => ({
  erros: items.filter(i => i.status === 'ERRO').length,
  alertas: items.filter(i => i.status === 'ALERTA').length,
  avisos: items.filter(i => i.status === 'AVISO').length,
});

const AuditoriaCaixaDetalhe: React.FC<Props> = ({ resultado }) => {
  const [itemExpandido, setItemExpandido] = useState<string | null>(null);
  const [fundamentacaoAberta, setFundamentacaoAberta] = useState(false);

  const saldoCredor = resultado.saldoCredor || [];
  const operacoesEspecie = resultado.operacoesEspecie || [];
  const beneficioIndireto = resultado.beneficioIndireto || [];

  // ── Agregados por seção (COR-51) ──────────────────────────────────────────
  // Cada análise tem natureza, base legal e critério fiscal distintos — os subtotais
  // deixam claro a origem de cada componente do impacto consolidado.
  const scFiscal = saldoCredor.reduce((sum, item) => sum + item.impactoFiscalTotal, 0);
  const dmeFiscal = operacoesEspecie.reduce((sum, item) => sum + item.multaDme, 0);
  const scContadores = contarStatus(saldoCredor);
  const dmeContadores = contarStatus(operacoesEspecie);
  const biContadores = contarStatus(beneficioIndireto);

  // ── Agregados de Benefício Indireto — COR-50 ──────────────────────────────
  // O cálculo fiscal é consequência da classificação; AVISO não entra no total.
  const biFiscais = beneficioIndireto.filter(item => temEvidenciaFiscal(item.tipoAchado));
  const biPendentes = beneficioIndireto.filter(item => !temEvidenciaFiscal(item.tipoAchado));
  // COR-270: "conciliado por devolução/estorno" já é uma conclusão (o par saída+devolução foi
  // encontrado e validado), não uma pendência — separado de biPendentes para que a tela nunca
  // mais diga "aguarda investigação documental"/"ausência de evidência suficiente" sobre um
  // lançamento que a própria regra já conciliou. Mantido em biPendentes (linha acima) para não
  // quebrar nenhum outro cálculo que dependa desse agregado — usado só nas mensagens abaixo.
  const biConciliadosPorDevolucao = beneficioIndireto.filter(item => item.tipoAchado === 'CONCILIADO_POR_DEVOLUCAO');
  const biRealmentePendentes = biPendentes.filter(item => item.tipoAchado !== 'CONCILIADO_POR_DEVOLUCAO');
  const impactoBiTotal = biFiscais.reduce((sum, item) => sum + item.impactoFiscalTotal, 0);

  const alternarExpandir = (chave: string) => {
    setItemExpandido(itemExpandido === chave ? null : chave);
  };

  // COR-240: nome do arquivo derivado do próprio item (empresaNome já vem em cada ocorrência) —
  // evita ter que alterar a assinatura do componente/AuditoriaPage.tsx só para isso.
  const nomeBaseBi = `beneficio_indireto_${(beneficioIndireto[0]?.empresaNome || 'empresa').replace(/[^\w-]+/g, '_')}`;

  const exportarBeneficioIndireto = (formato: 'csv' | 'xlsx', filtroStatus?: string) => {
    const itens = filtroStatus ? beneficioIndireto.filter(i => i.status === filtroStatus) : beneficioIndireto;
    const linhas = montarLinhasExportacaoBeneficioIndireto(itens, labelTipoAchado);
    const sufixo = filtroStatus ? `_${filtroStatus.toLowerCase()}` : '_tudo';
    if (formato === 'csv') exportarCSVBeneficioIndireto(linhas, `${nomeBaseBi}${sufixo}`);
    else exportarXLSXBeneficioIndireto(linhas, `${nomeBaseBi}${sufixo}`);
  };

  // Rótulo resumido por seção para uso no alerta superior
  const resumoSecoes = [
    saldoCredor.length > 0 && `Saldo Credor (${saldoCredor.length})`,
    operacoesEspecie.length > 0 && `Espécie/DME (${operacoesEspecie.length})`,
    beneficioIndireto.length > 0 && `Benefício Indireto (${beneficioIndireto.length})`,
  ].filter(Boolean).join(' · ');

  return (
    <>
      <div className={`auditoria-alert status-${resultado.status}`}>
        <i className={`bi ${getIconeStatus(resultado.status)} auditoria-alert-icon`} />
        <div>
          <strong>Auditoria de Caixa, Espécie e Benefício Indireto — {resultado.status}</strong>
          <div>{resultado.mensagemDirecionamento}</div>
          {resumoSecoes && (
            <div className='mt-1' style={{ fontSize: '0.82rem', opacity: 0.85 }}>
              Análises com ocorrências: {resumoSecoes}
            </div>
          )}
        </div>
      </div>

      <div className='auditoria-kpis'>
        {/* ── KPI 1: Impacto Fiscal com breakdown por análise (COR-51) ───────── */}
        <div className={`auditoria-kpi ${resultado.impactoFiscalTotal > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Impacto Fiscal Estimado</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFiscalTotal)}</div>

          {/* Breakdown: cada análise contribui independentemente para o total */}
          <div className='auditoria-kpi-help mt-2' style={{ borderTop: '1px solid rgba(0,0,0,0.08)', paddingTop: '0.4rem' }}>
            {[
              { label: 'Saldo Credor', valor: scFiscal },
              { label: 'Espécie / DME', valor: dmeFiscal },
              { label: 'Benef. Indireto (est.)', valor: impactoBiTotal },
            ].map(({ label, valor }) => (
              <div key={label} className='d-flex justify-content-between' style={{ lineHeight: '1.6' }}>
                <span style={{ opacity: 0.8 }}>{label}</span>
                <span className={valor === 0 ? '' : 'fw-semibold'}>{formatCurrency(valor)}</span>
              </div>
            ))}
          </div>

          {/* COR-270: "conciliado por devolução/estorno" nunca é pendência — mensagem própria,
              nunca "aguardam investigação" sobre algo que a regra já conciliou. */}
          {resultado.impactoFiscalTotal === 0 && biRealmentePendentes.length > 0 && (
            <div className='auditoria-kpi-help mt-1' style={{ color: '#b45309' }}>
              Sem impacto confirmado — {biRealmentePendentes.length} pendência{biRealmentePendentes.length !== 1 ? 's' : ''} aguardam investigação
            </div>
          )}
          {resultado.impactoFiscalTotal === 0 && biRealmentePendentes.length === 0 && biConciliadosPorDevolucao.length > 0 && (
            <div className='auditoria-kpi-help mt-1' style={{ color: '#0f5132' }}>
              Sem impacto fiscal — {biConciliadosPorDevolucao.length} ocorrência{biConciliadosPorDevolucao.length !== 1 ? 's' : ''} conciliada{biConciliadosPorDevolucao.length !== 1 ? 's' : ''} por devolução/estorno
            </div>
          )}
          {resultado.impactoFiscalTotal === 0 && biRealmentePendentes.length === 0 && biConciliadosPorDevolucao.length === 0 && (
            <span className='auditoria-kpi-selo'>Zero Desperdício</span>
          )}
        </div>

        {/* ── KPI 2: Contagem de ocorrências com unidades e critério (COR-51/52) ── */}
        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Contagem de Ocorrências</div>

          {/* Totais consolidados — cada badge = soma das três análises */}
          <div className='auditoria-kpi-value'>
            <span className='auditoria-contador auditoria-contador-erro'>{resultado.totalErros}</span>{' '}
            <span className='auditoria-contador auditoria-contador-alerta'>{resultado.totalAlertas}</span>{' '}
            <span className='auditoria-contador auditoria-contador-aviso'>{resultado.totalAvisos}</span>
          </div>

          <div className='auditoria-kpi-help mt-2' style={{ borderTop: '1px solid rgba(0,0,0,0.08)', paddingTop: '0.4rem' }}>
            {/* Breakdown por análise com unidade de medida contextual */}
            {[
              {
                label: 'Saldo Credor',
                unidade: 'dias c/ saldo negativo',
                c: scContadores,
                n: saldoCredor.length,
              },
              {
                label: 'Espécie / DME',
                unidade: 'participante/competência',
                c: dmeContadores,
                n: operacoesEspecie.length,
              },
              {
                label: 'Benef. Indireto',
                unidade: 'lançamentos',
                c: biContadores,
                n: beneficioIndireto.length,
              },
            ]
              .filter(({ n }) => n > 0)
              .map(({ label, unidade, c, n }) => (
                <React.Fragment key={label}>
                  <div className='d-flex justify-content-between align-items-start' style={{ lineHeight: '1.65' }}>
                    <span>
                      {label}
                      <br />
                      <span style={{ opacity: 0.5, fontSize: '0.7rem' }}>{n} {unidade}</span>
                    </span>
                    <span className='d-flex align-items-center gap-1 ms-2'>
                      {c.erros > 0 && <span className='auditoria-contador auditoria-contador-erro'>{c.erros}</span>}
                      {c.alertas > 0 && <span className='auditoria-contador auditoria-contador-alerta'>{c.alertas}</span>}
                      {c.avisos > 0 && <span className='auditoria-contador auditoria-contador-aviso'>{c.avisos}</span>}
                      {c.erros + c.alertas + c.avisos === 0 && <span style={{ opacity: 0.4 }}>—</span>}
                    </span>
                  </div>

                  {/* COR-308: Benefício Indireto agrupa lançamentos de naturezas bem distintas
                      (exposição fiscal confirmada, pendente de investigação, ou já conciliado por
                      devolução/estorno sem exposição) — os badges ERRO/ALERTA/AVISO acima não
                      contam CONCILIADO_POR_DEVOLUCAO (Status="CONCILIADO"), então esta linha torna
                      as três categorias explícitas em vez de deixá-las implícitas na contagem. */}
                  {label === 'Benef. Indireto' && (
                    <div style={{ fontSize: '0.7rem', opacity: 0.75, marginTop: '-0.3rem', marginBottom: '0.4rem' }}>
                      {biFiscais.length} com exposição fiscal · {biRealmentePendentes.length} pendente{biRealmentePendentes.length !== 1 ? 's' : ''} de investigação · {biConciliadosPorDevolucao.length} conciliada{biConciliadosPorDevolucao.length !== 1 ? 's' : ''} por devolução/estorno
                    </div>
                  )}
                </React.Fragment>
              ))}

            {/* Legenda de critério por status */}
            <div style={{ borderTop: '1px solid rgba(0,0,0,0.06)', marginTop: '0.45rem', paddingTop: '0.35rem' }}>
              {([
                { cls: 'erro', texto: 'confirmado — impacto fiscal apurado' },
                { cls: 'alerta', texto: 'suspeita ou exposição estimada' },
                { cls: 'aviso', texto: 'atenção — sem impacto fiscal' },
              ] as const).map(({ cls, texto }) => (
                <div key={cls} style={{ lineHeight: '1.55' }}>
                  <span className={`auditoria-contador auditoria-contador-${cls} me-1`} style={{ fontSize: '0.65rem' }}>
                    {cls.toUpperCase()}
                  </span>
                  <span>{texto}</span>
                </div>
              ))}
            </div>

            {/* Alinhamento: reforça que o contador espelha as linhas das tabelas abaixo */}
            <div style={{ marginTop: '0.35rem', opacity: 0.45, fontSize: '0.69rem' }}>
              Cada linha nas tabelas abaixo = 1 ocorrência neste indicador
            </div>
          </div>
        </div>

        {/* ── KPI 3: Fundamentação (inalterado) ──────────────────────────────── */}
        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFundamentacaoAberta(!fundamentacaoAberta)}
            >
              <i className='bi bi-journal-check me-1' />
              {fundamentacaoAberta ? 'Ocultar fundamentação' : 'Ver fundamentação legal'}
            </button>
          </div>
          <div className='auditoria-kpi-help'>Base legal aplicada às três análises desta auditoria.</div>
        </div>
      </div>

      {fundamentacaoAberta && (
        <div className='auditoria-card'>
          <div><strong>Saldo Credor de Caixa:</strong> {resultado.fundamentacaoSaldoCredor}</div>
          <div className='mt-2'><strong>Operações em Espécie / DME:</strong> {resultado.fundamentacaoEspecie}</div>
          <div className='mt-2'><strong>Benefício Indireto:</strong> {resultado.fundamentacaoBeneficioIndireto}</div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════════
          ANÁLISE 1 — Saldo Credor de Caixa
          Base: Art. 40 da Lei nº 9.430/1996; Art. 3º da Lei nº 8.137/1990
          Critério: saldo devedor em conta de Ativo (Caixa) no razão da ECD
          ════════════════════════════════════════════════════════════════════════ */}
      <div className='auditoria-card'>
        <h3>Saldo Credor de Caixa — Presunção de Omissão de Receita</h3>

        {/* Subtotal da seção (COR-51) */}
        {saldoCredor.length > 0 && (
          <div className='d-flex flex-wrap gap-3 align-items-center mt-1 mb-2' style={{ fontSize: '0.82rem' }}>
            <span className='text-muted'>{saldoCredor.length} ocorrência{saldoCredor.length !== 1 ? 's' : ''}</span>
            <span className='text-muted'>·</span>
            <span className='text-muted'>Impacto: <strong className='text-body'>{formatCurrency(scFiscal)}</strong></span>
            {scContadores.erros > 0 && <span className='auditoria-contador auditoria-contador-erro'>{scContadores.erros} ERRO</span>}
            {scContadores.alertas > 0 && <span className='auditoria-contador auditoria-contador-alerta'>{scContadores.alertas} ALERTA</span>}
            {scContadores.avisos > 0 && <span className='auditoria-contador auditoria-contador-aviso'>{scContadores.avisos} AVISO</span>}
          </div>
        )}

        <p className='text-muted'>Clique em uma ocorrência para ver a memória de cálculo completa.</p>

        {saldoCredor.length === 0 && (
          <div className='text-muted mt-4'>Nenhuma ocorrência de saldo credor identificada nas contas de Caixa/Disponível no período.</div>
        )}

        {saldoCredor.length > 0 && (
          <div className='table-responsive mt-2'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Data</th>
                  <th className='text-end'>Saldo Credor</th>
                  <th className='text-end'>Impacto Fiscal</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {saldoCredor.map((item: IItemSaldoCredorCaixa, idx) => {
                  const chave = `sc-${item.empresaId}-${item.contaContabil}-${item.dataEvento}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>
                          {item.descricaoConta || '-'}
                          {item.contaContabil && <div className='text-muted' style={{ fontSize: '0.75rem' }}>{item.contaContabil}</div>}
                        </td>
                        <td>{formatDate(item.dataEvento)}</td>
                        <td className='text-end'>{formatCurrency(Math.abs(item.saldoCaixa))}</td>
                        <td className='text-end'>{formatCurrency(item.impactoFiscalTotal)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={5} style={{ background: '#fafbfc' }}>
                            <div className='py-2 px-3'>
                              <div><strong>Diagnóstico:</strong> {item.mensagemDiagnostico}</div>
                              <div><strong>Memória de Cálculo:</strong> {item.memoriaCalculo}</div>
                              <div><strong>Dias com saldo credor no período:</strong> {item.diasConsecutivosCredor}</div>
                              <BlocoLancamentosRelacionados lancamentos={item.lancamentosRelacionados} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ════════════════════════════════════════════════════════════════════════
          ANÁLISE 2 — Operações em Espécie / DME
          Base: IN RFB nº 1.761/2017; Lei nº 9.613/1998 (Lavagem de Dinheiro)
          Critério: movimentação de dinheiro físico por participante/mês acima
          dos limites preventivos
          ════════════════════════════════════════════════════════════════════════ */}
      <div className='auditoria-card'>
        <h3>Operações em Espécie — DME</h3>

        {/* Subtotal da seção (COR-51) */}
        {operacoesEspecie.length > 0 && (
          <div className='d-flex flex-wrap gap-3 align-items-center mt-1 mb-2' style={{ fontSize: '0.82rem' }}>
            <span className='text-muted'>{operacoesEspecie.length} ocorrência{operacoesEspecie.length !== 1 ? 's' : ''}</span>
            <span className='text-muted'>·</span>
            <span className='text-muted'>Multa: <strong className='text-body'>{formatCurrency(dmeFiscal)}</strong></span>
            {dmeContadores.erros > 0 && <span className='auditoria-contador auditoria-contador-erro'>{dmeContadores.erros} ERRO</span>}
            {dmeContadores.alertas > 0 && <span className='auditoria-contador auditoria-contador-alerta'>{dmeContadores.alertas} ALERTA</span>}
            {dmeContadores.avisos > 0 && <span className='auditoria-contador auditoria-contador-aviso'>{dmeContadores.avisos} AVISO</span>}
          </div>
        )}

        <p className='text-muted'>Movimentação em dinheiro físico por participante/mês, comparada aos limites da IN RFB nº 1.761/2017.</p>

        {operacoesEspecie.length === 0 && (
          <div className='text-muted mt-4'>Nenhuma operação em espécie acima da zona preventiva (R$ 25.000,00) identificada no período.</div>
        )}

        {operacoesEspecie.length > 0 && (
          <div className='table-responsive mt-2'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Participante</th>
                  <th>Conta</th>
                  <th>Competência</th>
                  <th className='text-end'>Total em Espécie</th>
                  <th className='text-end'>Multa DME</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {operacoesEspecie.map((item: IItemOperacaoEspecie, idx) => {
                  const chave = `es-${item.empresaId}-${item.participante}-${item.competencia}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>{item.participante}{item.documentoParticipante ? ` (${item.documentoParticipante})` : ''}</td>
                        <td>
                          {item.descricaoConta || '-'}
                          {item.contaContabil && <div className='text-muted' style={{ fontSize: '0.75rem' }}>{item.contaContabil}</div>}
                        </td>
                        <td>{item.competencia}</td>
                        <td className='text-end'>{formatCurrency(item.totalMovimentadoEspecie)}</td>
                        <td className='text-end'>{formatCurrency(item.multaDme)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={6} style={{ background: '#fafbfc' }}>
                            <div className='py-2 px-3'>
                              <div><strong>Diagnóstico:</strong> {item.mensagemDiagnostico}</div>
                              <div><strong>Limite aplicado:</strong> {formatCurrency(item.limiteAplicado)}</div>
                              <BlocoLancamentosRelacionados lancamentos={item.lancamentosRelacionados} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ════════════════════════════════════════════════════════════════════════
          ANÁLISE 3 — Benefício Indireto
          Base: Art. 61 da Lei nº 8.981/1995 (IRRF gross-up); Art. 22, I,
          da Lei nº 8.212/1991 (INSS patronal); Art. 12 da Lei nº 8.218/1991
          (Multa SPED)
          Critério: saída de Caixa sem identificação de beneficiário após
          filtragem de natureza operacional, contrapartida e histórico
          ════════════════════════════════════════════════════════════════════════ */}
      <div className='auditoria-card'>
        <h3>Benefício Indireto</h3>

        {/* Subtotal da seção (COR-51) */}
        {beneficioIndireto.length > 0 && (
          <div className='d-flex flex-wrap gap-3 align-items-center mt-1 mb-2' style={{ fontSize: '0.82rem' }}>
            <span className='text-muted'>{beneficioIndireto.length} ocorrência{beneficioIndireto.length !== 1 ? 's' : ''}</span>
            <span className='text-muted'>·</span>
            <span className='text-muted'>
              Exp. estimada: <strong className='text-body'>{formatCurrency(impactoBiTotal)}</strong>
              {biFiscais.length > 0 && ` (${biFiscais.length} com evidência)`}
            </span>
            {biContadores.alertas > 0 && <span className='auditoria-contador auditoria-contador-alerta'>{biContadores.alertas} ALERTA</span>}
            {biContadores.avisos > 0 && <span className='auditoria-contador auditoria-contador-aviso'>{biContadores.avisos} AVISO</span>}
          </div>
        )}

        <p className='text-muted'>Saídas de Caixa/Disponível sem identificação de beneficiário no histórico do lançamento.</p>

        {beneficioIndireto.length === 0 && (
          <div className='text-muted mt-4'>Nenhuma saída de Caixa sem identificação de beneficiário identificada no período.</div>
        )}

        {beneficioIndireto.length > 0 && (
          <>
            {/* Banner resumo fiscal — condicional à classificação (COR-50). COR-270: lançamentos
                conciliados por devolução/estorno nunca entram na contagem de "pendente de
                investigação" — têm mensagem própria, já que a regra já os validou; mantidos na
                listagem abaixo só para rastreabilidade. */}
            <div className='mt-2 mb-3'>
              {biFiscais.length > 0 ? (
                <div className='text-muted small'>
                  Exposição fiscal estimada: <strong>{formatCurrency(impactoBiTotal)}</strong> referente a{' '}
                  {biFiscais.length} lançamento{biFiscais.length !== 1 ? 's' : ''} com evidência de possível benefício indireto.
                  {biRealmentePendentes.length > 0 && (
                    <> Mais {biRealmentePendentes.length} lançamento{biRealmentePendentes.length !== 1 ? 's' : ''} pendente{biRealmentePendentes.length !== 1 ? 's' : ''} de investigação (sem impacto calculado).</>
                  )}
                  {biConciliadosPorDevolucao.length > 0 && (
                    <> Mais {biConciliadosPorDevolucao.length} lançamento{biConciliadosPorDevolucao.length !== 1 ? 's' : ''} conciliado{biConciliadosPorDevolucao.length !== 1 ? 's' : ''} por devolução/estorno, sem exposição fiscal apurada (mantido{biConciliadosPorDevolucao.length !== 1 ? 's' : ''} apenas para rastreabilidade).</>
                  )}
                </div>
              ) : biRealmentePendentes.length > 0 ? (
                <div className='alert alert-info mb-0 py-2'>
                  <i className='bi bi-info-circle me-2' />
                  Impacto fiscal não calculado por ausência de evidência suficiente.{' '}
                  {biRealmentePendentes.length} lançamento{biRealmentePendentes.length !== 1 ? 's' : ''} identificado{biRealmentePendentes.length !== 1 ? 's' : ''} aguarda{biRealmentePendentes.length !== 1 ? 'm' : ''} investigação documental.
                  {biConciliadosPorDevolucao.length > 0 && (
                    <> Mais {biConciliadosPorDevolucao.length} lançamento{biConciliadosPorDevolucao.length !== 1 ? 's' : ''} conciliado{biConciliadosPorDevolucao.length !== 1 ? 's' : ''} por devolução/estorno, sem exposição fiscal apurada.</>
                  )}
                </div>
              ) : (
                <div className='alert alert-success mb-0 py-2'>
                  <i className='bi bi-check-circle me-2' />
                  {biConciliadosPorDevolucao.length} lançamento{biConciliadosPorDevolucao.length !== 1 ? 's' : ''} conciliado{biConciliadosPorDevolucao.length !== 1 ? 's' : ''} por devolução/estorno, sem exposição fiscal apurada. Mantido{biConciliadosPorDevolucao.length !== 1 ? 's' : ''} na listagem apenas para rastreabilidade.
                </div>
              )}
            </div>

            {/* COR-240: exportação das ocorrências — data, conta, histórico original, valor,
                lançamento ECD original, lançamento de contrapartida, histórico da contrapartida,
                beneficiário identificado, classificação e justificativa. */}
            <div className='d-flex flex-wrap gap-2 align-items-center mb-3'>
              <strong style={{ marginRight: 4, fontSize: '0.85rem' }}>
                <i className='bi bi-download me-1' />
                Exportar ocorrências:
              </strong>
              <button type='button' className='btn btn-sm btn-outline-primary' onClick={() => exportarBeneficioIndireto('csv')}>
                Tudo (CSV) — {beneficioIndireto.length}
              </button>
              <button type='button' className='btn btn-sm btn-outline-primary' onClick={() => exportarBeneficioIndireto('xlsx')}>
                Tudo (XLSX)
              </button>
              {biContadores.alertas > 0 && (
                <button type='button' className='btn btn-sm btn-outline-warning' onClick={() => exportarBeneficioIndireto('csv', 'ALERTA')}>
                  Alertas (CSV) — {biContadores.alertas}
                </button>
              )}
              {biContadores.avisos > 0 && (
                <button type='button' className='btn btn-sm btn-outline-secondary' onClick={() => exportarBeneficioIndireto('csv', 'AVISO')}>
                  Avisos (CSV) — {biContadores.avisos}
                </button>
              )}
            </div>

            <div className='table-responsive'>
              <table className='auditoria-table'>
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Conta</th>
                    <th>Histórico</th>
                    <th className='text-end'>Valor</th>
                    <th className='text-end'>Impacto Estimado</th>
                    <th>Classificação</th>
                  </tr>
                </thead>
                <tbody>
                  {beneficioIndireto.map((item: IItemBeneficioIndireto, idx) => {
                    const chave = `bi-${item.empresaId}-${item.contaContabil}-${item.dataLancamento}-${idx}`;
                    const expandido = itemExpandido === chave;
                    const comFiscal = temEvidenciaFiscal(item.tipoAchado);

                    return (
                      <React.Fragment key={chave}>
                        <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                          <td>{formatDate(item.dataLancamento)}</td>
                          <td>
                            {item.descricaoConta || '-'}
                            {item.contaContabil && <div className='text-muted' style={{ fontSize: '0.75rem' }}>{item.contaContabil}</div>}
                          </td>
                          <td>{item.historico || '-'}</td>
                          <td className='text-end'>{formatCurrency(item.valor)}</td>
                          <td className='text-end'>
                            {comFiscal ? (
                              formatCurrency(item.impactoFiscalTotal)
                            ) : (
                              <span className='text-muted fst-italic'>Não calculado</span>
                            )}
                          </td>
                          <td>
                            <div className='d-flex align-items-center gap-2 flex-wrap'>
                              <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                              <span className='text-muted small'>{labelTipoAchado(item.tipoAchado)}</span>
                              <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-1 text-muted`} />
                            </div>
                          </td>
                        </tr>

                        {expandido && (
                          <tr>
                            <td colSpan={6} style={{ background: '#fafbfc' }}>
                              <div className='py-2 px-3'>
                                <div><strong>Diagnóstico:</strong> {item.mensagemDiagnostico}</div>
                                <div><strong>Memória de Cálculo:</strong> {item.memoriaCalculo}</div>
                                {item.numeroLancamento && (
                                  <div><strong>Nº Lançamento (original):</strong> {item.numeroLancamento}</div>
                                )}

                                {/* COR-226: memória de cálculo estruturada do vínculo devolução/estorno */}
                                {item.devolucaoVinculada && (
                                  <div className='mt-2 p-2' style={{ background: '#eff6ff', borderRadius: 6, border: '1px solid #bfdbfe' }}>
                                    <div className='mb-1'>
                                      <i className='bi bi-arrow-repeat me-1' />
                                      <strong>Devolução/Estorno Vinculado</strong>
                                    </div>
                                    <table className='table table-sm mb-1' style={{ fontSize: '0.82rem' }}>
                                      <tbody>
                                        <tr><td>Nº Lançamento</td><td>{item.devolucaoVinculada.numeroLancamentoDevolucao || '-'}</td></tr>
                                        <tr><td>Conta Contábil</td><td>{item.devolucaoVinculada.contaContabil}</td></tr>
                                        <tr><td>Data</td><td>{formatDate(item.devolucaoVinculada.dataDevolucao)}</td></tr>
                                        <tr><td>Valor</td><td>{formatCurrency(item.devolucaoVinculada.valorDevolucao)}</td></tr>
                                        <tr><td>Histórico</td><td>{item.devolucaoVinculada.historicoDevolucao}</td></tr>
                                        <tr><td>Beneficiário Identificado</td><td>{item.devolucaoVinculada.beneficiarioIdentificado}</td></tr>
                                      </tbody>
                                    </table>
                                    <div className='text-muted' style={{ fontSize: '0.78rem' }}>
                                      <strong>Vínculo utilizado:</strong> {item.devolucaoVinculada.criterioVinculo}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default AuditoriaCaixaDetalhe;
